import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import TicketModal from './TicketModal';
import { API_URL } from '../api/base';

interface Settings {
  tickets_enabled: string;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [hasBlogPosts, setHasBlogPosts] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/settings`);
        setSettings(data.settings);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    })();

    (async () => {
      try {
        const { data } = await axios.get<{ posts: unknown[] }>(`${API_URL}/api/blog`, {
          params: { limit: 1 }
        });
        setHasBlogPosts(Array.isArray(data.posts) && data.posts.length > 0);
      } catch {
        setHasBlogPosts(false);
      }
    })();
  }, []);

  const getCurrentLang = () => {
    const lang = i18n.language || 'ru';
    if (lang.startsWith('ky')) return 'ky';
    if (lang.startsWith('en')) return 'en';
    return 'ru';
  };
  const current = getCurrentLang();

  const languages = [
    { code: 'ru', label: 'ру', name: 'Русский' },
    { code: 'ky', label: 'кг', name: 'Кыргызча' },
    { code: 'en', label: 'en', name: 'English' }
  ];
  const currentLangData = languages.find(l => l.code === current) || languages[0];

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem('lng', lng); } catch {}
    setLangMenuOpen(false);
  };

  const homeAnchor = (hash: string) => (isHomePage ? `#${hash}` : `/#${hash}`);

  return (
    <header
      className="sticky top-0 z-50"
      style={{
        background: '#5EA82E',
        boxShadow: scrolled ? '0 4px 16px rgba(0,0,0,.15)' : 'none',
        transition: 'box-shadow 200ms ease'
      }}
    >
      <div className="max-w-[1200px] mx-auto h-[76px] px-6 flex items-center justify-between">
        {/* ЛОГО */}
        <div className="flex items-center gap-[11px]">
          <Link to="/" className="flex items-center gap-[11px]">
            <img src="/uploads/jashyl-logo-white.svg" alt="Жашыл Дем" className="h-[38px]" />
            <span
              className="text-white"
              style={{ font: "700 20px 'Montserrat Alternates', sans-serif", letterSpacing: '-0.01em' }}
            >
              Жашыл дем
            </span>
          </Link>

          {/* Language selector */}
          <div className="relative ml-1">
            <button
              type="button"
              onClick={() => setLangMenuOpen(v => !v)}
              className="text-white uppercase"
              style={{
                borderRadius: 10,
                background: 'rgba(255,255,255,.14)',
                boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,.5)',
                font: "500 12px 'Golos Text', sans-serif",
                padding: '7px 11px'
              }}
              aria-label="Выбрать язык"
            >
              {currentLangData.label}
            </button>

            {langMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
                <div className="absolute top-full mt-2 left-0 bg-white rounded-lg shadow-lg overflow-hidden z-50 min-w-[140px]">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-tint/40 transition text-left ${
                        current === lang.code ? 'bg-tint/60' : ''
                      }`}
                    >
                      <span className="text-gray-500 uppercase font-medium text-xs">{lang.label}</span>
                      <span className="text-gray-800 text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center" style={{ gap: 26 }}>
          <a
            href={homeAnchor('why')}
            className="text-white hover:opacity-85"
            style={{ font: "500 15px 'Golos Text', sans-serif" }}
          >
            {t('header.why')}
          </a>
          <a
            href={homeAnchor('team')}
            className="text-white hover:opacity-85"
            style={{ font: "500 15px 'Golos Text', sans-serif" }}
          >
            {t('header.about')}
          </a>
          <a
            href={homeAnchor('contacts')}
            className="text-white hover:opacity-85"
            style={{ font: "500 15px 'Golos Text', sans-serif" }}
          >
            {t('header.contacts')}
          </a>
          {hasBlogPosts && (
            <Link
              to="/blog"
              className="text-white hover:opacity-85"
              style={{ font: "500 15px 'Golos Text', sans-serif" }}
            >
              {t('header.blog')}
            </Link>
          )}
          {settings?.tickets_enabled === 'true' && (
            <button
              type="button"
              onClick={() => setTicketModalOpen(true)}
              className="text-white hover:opacity-85"
              style={{ font: "500 15px 'Golos Text', sans-serif" }}
            >
              {t('header.buy_ticket')}
            </button>
          )}
          <a href={homeAnchor('hero')} className="header-donate-btn">
            {t('header.donate')}
          </a>
        </nav>

        {/* Mobile burger */}
        <button
          onClick={() => setMenuOpen(v => !v)}
          className="md:hidden text-3xl text-white focus:outline-none"
          aria-label="Открыть меню"
        >
          ☰
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="mobile-menu md:hidden text-white px-6 py-4 space-y-3">
          <div className="flex gap-2">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => { changeLang(lang.code); setMenuOpen(false); }}
                className={`px-3 py-1.5 rounded-lg text-white uppercase font-medium text-xs transition ${
                  current === lang.code
                    ? 'bg-white/25'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
                style={{ boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,.5)' }}
              >
                {lang.label}
              </button>
            ))}
          </div>
          <a href={homeAnchor('why')} onClick={() => setMenuOpen(false)} className="block hover:opacity-85 text-base">
            {t('header.why')}
          </a>
          <a href={homeAnchor('team')} onClick={() => setMenuOpen(false)} className="block hover:opacity-85 text-base">
            {t('header.about')}
          </a>
          <a href={homeAnchor('contacts')} onClick={() => setMenuOpen(false)} className="block hover:opacity-85 text-base">
            {t('header.contacts')}
          </a>
          {hasBlogPosts && (
            <Link to="/blog" onClick={() => setMenuOpen(false)} className="block hover:opacity-85 text-base">
              {t('header.blog')}
            </Link>
          )}
          {settings?.tickets_enabled === 'true' && (
            <button
              onClick={() => { setTicketModalOpen(true); setMenuOpen(false); }}
              className="block hover:opacity-85 text-left text-base"
            >
              {t('header.buy_ticket')}
            </button>
          )}
          <a
            href={homeAnchor('hero')}
            onClick={() => setMenuOpen(false)}
            className="header-donate-btn inline-block"
          >
            {t('header.donate')}
          </a>
        </div>
      )}

      {settings?.tickets_enabled === 'true' && (
        <TicketModal open={ticketModalOpen} onClose={() => setTicketModalOpen(false)} />
      )}
    </header>
  );
}

export default Header;
